import { useCallback, useMemo, useState } from "react";
import { t } from "ttag";

import type { DashboardBranding } from "agavepulse/theme/branding-types";
import { Box, Button, Group, Stack, Text, TextInput } from "metabase/ui";

import { BrandingPreview } from "./BrandingPreview";
import { ChartColorsField } from "./ChartColorsField";
import { isValidHexColor, isValidLogoUrl } from "./validation";

export interface BrandingSettingsProps {
  branding?: DashboardBranding | null;
  disabled?: boolean;
  onChange: (branding: DashboardBranding | null) => void;
}

/* eslint-disable-next-line metabase/no-color-literals -- an example hex in help text, not a rendered color. */
const HEX_COLOR_HINT = t`Enter a hex color, for example #193759.`;

type ColorField = "brandColor" | "filterColor" | "summarizeColor";

const COLOR_FIELDS: { key: ColorField; label: string; description: string }[] =
  [
    {
      key: "brandColor",
      label: t`Brand color`,
      description: t`Buttons, links and highlights.`,
    },
    {
      key: "filterColor",
      label: t`Filter color`,
      description: t`Filter widgets and their chips.`,
    },
    {
      key: "summarizeColor",
      label: t`Summarize color`,
      description: t`Aggregation and grouping controls.`,
    },
  ];

/**
 * The "Branding" section of dashboard settings: gives a non-technical user the same
 * control the API exposes, so branding a client dashboard never requires a curl command.
 *
 * Validation mirrors `src/metabase/agavepulse/branding.clj`. The server is still the
 * authority -- this only exists so a mistake surfaces as inline text while typing instead
 * of a 400 on save.
 */
export function BrandingSettings({
  branding,
  disabled,
  onChange,
}: BrandingSettingsProps) {
  const [logoDraft, setLogoDraft] = useState(branding?.logoUrl ?? "");

  // Colors need local draft state for the same reason the logo does: these are
  // controlled inputs, and propagating only valid hex would mean "#" never renders,
  // so the user could never type their way to a complete color.
  const [chartDrafts, setChartDrafts] = useState<string[]>(
    branding?.chartColors ?? [],
  );

  const [colorDrafts, setColorDrafts] = useState<Record<ColorField, string>>({
    brandColor: branding?.brandColor ?? "",
    filterColor: branding?.filterColor ?? "",
    summarizeColor: branding?.summarizeColor ?? "",
  });

  const logoError = useMemo(
    () =>
      logoDraft && !isValidLogoUrl(logoDraft)
        ? t`Enter an https:// URL.`
        : undefined,
    [logoDraft],
  );

  const update = useCallback(
    (patch: Partial<DashboardBranding>) => {
      const next = { ...(branding ?? {}), ...patch };

      // Drop empty values rather than storing nulls, so a dashboard that has been
      // cleared reads back as "no branding" and falls through to the AGAVE theme.
      const cleaned = Object.fromEntries(
        Object.entries(next).filter(([, value]) =>
          Array.isArray(value) ? value.length > 0 : Boolean(value),
        ),
      ) as DashboardBranding;

      onChange(Object.keys(cleaned).length > 0 ? cleaned : null);
    },
    [branding, onChange],
  );

  const handleColorChange = useCallback(
    (key: ColorField, value: string) => {
      setColorDrafts((drafts) => ({ ...drafts, [key]: value }));

      const trimmed = value.trim();

      // Clearing the field removes the override. A partially-typed value such as "#19"
      // is held in the draft but not propagated, so the dashboard behind the sidesheet
      // never flickers through nonsense colors mid-keystroke.
      if (trimmed === "") {
        update({ [key]: undefined });
        return;
      }
      if (isValidHexColor(trimmed)) {
        update({ [key]: trimmed });
      }
    },
    [update],
  );

  const handleLogoBlur = useCallback(() => {
    if (!logoDraft) {
      update({ logoUrl: undefined });
      return;
    }
    if (isValidLogoUrl(logoDraft)) {
      update({ logoUrl: logoDraft });
    }
  }, [logoDraft, update]);

  const handleReset = useCallback(() => {
    setLogoDraft("");
    setColorDrafts({ brandColor: "", filterColor: "", summarizeColor: "" });
    setChartDrafts([]);
    onChange(null);
  }, [onChange]);

  const hasBranding = Boolean(branding && Object.keys(branding).length > 0);

  return (
    <Stack gap="md">
      <Text size="sm" c="text-medium">
        {t`Colors and a logo for this dashboard only. Anything left empty uses the AGAVE default.`}
      </Text>

      {/* A labelled TextInput plus a swatch, rather than the shared ColorPicker:
          ColorPickerTrigger spreads props onto its wrapper Group and forwards only
          value/placeholder/onChange to the input, so no id or aria-label can reach it.
          Three fields all reading as "AGAVE default" to a screen reader is not
          acceptable, and patching that shared component would be a merge conflict. */}
      {COLOR_FIELDS.map(({ key, label, description }) => {
        const value = colorDrafts[key];
        const invalid = value.length > 0 && !isValidHexColor(value);

        return (
          <Group key={key} align="flex-end" wrap="nowrap" gap="sm">
            <TextInput
              flex={1}
              label={label}
              description={description}
              placeholder={t`AGAVE default`}
              value={value}
              disabled={disabled}
              error={invalid ? HEX_COLOR_HINT : undefined}
              onChange={(event) =>
                handleColorChange(key, event.currentTarget.value)
              }
            />
            <Box
              aria-hidden
              w="2.25rem"
              h="2.25rem"
              style={{
                flexShrink: 0,
                borderRadius: "0.25rem",
                border: "1px solid var(--mb-color-border)",
                background: isValidHexColor(value)
                  ? value
                  : "var(--mb-color-background-light)",
              }}
            />
          </Group>
        );
      })}

      <TextInput
        label={t`Logo URL`}
        description={t`Shown instead of the AGAVE mark. Must be served over https.`}
        placeholder="https://example.com/logo.svg"
        value={logoDraft}
        error={logoError}
        disabled={disabled}
        onChange={(event) => setLogoDraft(event.currentTarget.value)}
        onBlur={handleLogoBlur}
      />

      <ChartColorsField
        value={chartDrafts}
        disabled={disabled}
        onChange={(colors) => {
          setChartDrafts(colors);
          // Only propagate complete colors; a half-typed "#0a" would otherwise blank a
          // series mid-keystroke. Empty slots fall back to the AGAVE defaults.
          const valid = colors.filter((c) => isValidHexColor(c));
          update({ chartColors: valid.length > 0 ? valid : undefined });
        }}
      />

      <BrandingPreview branding={branding} />

      <Group justify="flex-end">
        <Button
          variant="subtle"
          disabled={disabled || !hasBranding}
          onClick={handleReset}
        >
          {t`Reset to AGAVE branding`}
        </Button>
      </Group>
    </Stack>
  );
}
